import { z } from "zod";
import { AssetType, PoolStatus } from "@/constants/enums";
import { isValidCalendarDay } from "@/utils/dateUtils";

/**
 * Invalid optional search params recover to `undefined` instead of throwing.
 * Bookmarked / stale filter URLs load the page without that filter.
 */
export function optionalSearch<T extends z.ZodType>(schema: T) {
  return schema.optional().catch(undefined);
}

/** Shared pool deep-link context for Assets Overview and Pool Activity. */
export const poolContextSearchSchema = z.object({
  poolID: optionalSearch(z.string()),
  merchantID: optionalSearch(z.string()),
  poolStatus: optionalSearch(
    z.enum([
      PoolStatus.Onboarding,
      PoolStatus.Open,
      PoolStatus.Active,
      PoolStatus.Closed,
    ]),
  ),
  assetType: optionalSearch(
    z.enum([AssetType.Inventory, AssetType.Receivable]),
  ),
});

export type PoolContextSearch = z.infer<typeof poolContextSearchSchema>;

/**
 * Timezone-free calendar day in URL search (`YYYY-MM-DD`).
 * Validated via {@link isValidCalendarDay} (shape + real local calendar day).
 */
export const calendarDaySchema = z.string().refine(isValidCalendarDay, {
  message: "Invalid calendar day",
});

export const optionalCalendarDay = optionalSearch(calendarDaySchema);

export const optionalPage = optionalSearch(z.number().int().nonnegative());

export const pageSearchSchema = z.object({
  page: optionalPage,
});

/** Shared defaults for stripSearchParams (`page` applied at read time via `??`). */
export const defaultPageSearch = { page: 0 } as const;
